// components/PositionDetailModal.jsx
import React, { useState, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import MarketChart from './MarketChart';

// ─── Dashboard palette ────────────────────────────────────────────────────────
const C = {
  bg:     '#08101E',
  bg1:    '#0C1525',
  bg2:    '#101B2E',
  bg3:    '#152030',
  border: '#1E2D42',
  text:   '#E8EDF5',
  body:   '#B8C8DC',
  meta:   '#6A7D95',
  cyan:   '#00C8FF',
  green:  '#00E896',
  amber:  '#FFB340',
  red:    '#FF4466',
  blue:   '#4488FF',
  purple: '#9966FF',
  font:   "'DM Mono', monospace",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtTime = ts => {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};
const fmtDate = ts => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtNum = (n, dp = 2) => (n == null || isNaN(n) ? '—' : Number(n).toFixed(dp));

const decisionColor = d => {
  switch (d) {
    case 'PROCEED': case 'EXECUTE': return C.green;
    case 'SKIP':    return C.meta;
    case 'ADJUST':  return C.amber;
    case 'HOLD':    return C.cyan;
    case 'CLOSE':   return C.red;
    default:        return C.meta;
  }
};
const decisionIcon = d => {
  switch (d) {
    case 'PROCEED': case 'EXECUTE': return '✓';
    case 'SKIP':    return '⊗';
    case 'ADJUST':  return '△';
    case 'HOLD':    return '⏸';
    case 'CLOSE':   return '✕';
    default:        return '·';
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const Label = ({ children }) => (
  <div style={{ fontFamily: C.font, fontSize: 9, color: C.meta, letterSpacing: '0.8px', marginBottom: 3 }}>
    {children}
  </div>
);

const Value = ({ children, color, size = 13 }) => (
  <div style={{ fontFamily: C.font, fontSize: size, color: color || C.text, fontWeight: 500 }}>
    {children}
  </div>
);

const Pill = ({ children, color }) => (
  <span style={{
    fontFamily: C.font, fontSize: 9, letterSpacing: '0.6px',
    color: color || C.meta,
    background: `${color || C.meta}18`,
    border: `1px solid ${color || C.meta}50`,
    borderRadius: 4, padding: '2px 8px',
  }}>
    {children}
  </span>
);

const Divider = () => (
  <div style={{ height: 1, background: C.border, opacity: 0.5, margin: '16px 0' }} />
);

const Section = ({ title, children }) => (
  <div style={{
    background: C.bg2, border: `1px solid ${C.border}`,
    borderRadius: 10, padding: '16px 20px', marginBottom: 12,
  }}>
    <div style={{ fontFamily: C.font, fontSize: 9, color: C.meta, letterSpacing: '1px', marginBottom: 12 }}>
      {title}
    </div>
    {children}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const PositionDetailModal = ({
  position, chartData, evaluations = [],
  show, onHide, onViewFullStory, onEvaluationSelect,
}) => {
  const [expandedEval, setExpandedEval] = useState(null);

  // ── Evaluations — must be before early return (hooks rules) ─────────────────
  const positionEvaluations = useMemo(() => {
    if (!evaluations?.length || !position) return [];
    const entry = new Date(position.entryTime);
    const exit  = position.exitTime ? new Date(position.exitTime) : new Date();
    return evaluations
      .filter(e => {
        const t = new Date(e.timestamp);
        const inWindow = t >= entry && t <= exit;
        const matchesId = e.position_id === position.id || e.eval_position_id === position.id;
        return matchesId || inWindow;
      })
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [evaluations, position]);

  if (!position) return null;

  // ── Position metrics ────────────────────────────────────────────────────────
  const entryPrice   = parseFloat(position.entryPrice)  || 0;
  const exitPrice    = parseFloat(position.exitPrice || position.currentPrice) || 0;
  const currentPrice = parseFloat(position.currentPrice) || 0;
  const qty          = parseInt(position.qty)           || 0;
  const isLong       = position.isLong;
  const isClosed     = !!position.exitTime;
  const unrealizedPL = parseFloat(position.unrealizedPL) || 0;
  const isWin        = position.isWin != null ? position.isWin : unrealizedPL >= 0;
  const plPct        = entryPrice > 0 && qty > 0
    ? ((Math.abs(unrealizedPL) / (entryPrice * qty)) * 100) : 0;

  // Hold time
  const entryMs     = new Date(position.entryTime);
  const exitMs      = position.exitTime ? new Date(position.exitTime) : new Date();
  const holdMin     = Math.round((exitMs - entryMs) / 60000);
  const holdDisplay = holdMin >= 60
    ? `${Math.floor(holdMin / 60)}h ${holdMin % 60}m`
    : `${holdMin}m`;

  const proactiveEvals = positionEvaluations.filter(e => e.type === 'PROACTIVE');
  const reactiveEvals  = positionEvaluations.filter(e => e.type === 'REACTIVE');

  // ── Empty eval state logic ──────────────────────────────────────────────────
  const evalEmptyMessage = () => {
    if (isClosed && reactiveEvals.length === 0 && proactiveEvals.length === 0) {
      return {
        title: 'Position closed before evaluation ran.',
        detail: 'The trade resolved faster than the evaluation cycle — the agent\'s target or stop was hit before the next reactive evaluation was scheduled. This is normal for fast 0DTE exits.',
      };
    }
    if (isClosed && reactiveEvals.length === 0 && proactiveEvals.length > 0) {
      return {
        title: 'No reactive evaluations — trade closed at initial parameters.',
        detail: 'The agent set the target and stop at entry and the position resolved without requiring a hold, adjust, or close decision.',
      };
    }
    return {
      title: 'No evaluations found for this position.',
      detail: 'Evaluation data may still be loading or was not recorded for this position.',
    };
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" fullscreen="lg-down" centered scrollable>
      <div style={{ background: C.bg, fontFamily: C.font, borderRadius: 8, overflow: 'hidden' }}>

        {/* ── Header ── */}
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{ background: C.bg1, borderBottom: `1px solid ${C.border}`, padding: '16px 24px' }}
        >
          <Modal.Title style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: C.font, fontSize: 18, fontWeight: 600, color: C.text }}>
                {position.symbol}
              </span>
              <Pill color={isLong ? C.green : C.red}>{isLong ? 'LONG' : 'SHORT'}</Pill>
              <Pill color={isClosed ? C.meta : C.cyan}>{isClosed ? 'CLOSED' : 'ACTIVE'}</Pill>
              {isClosed && <Pill color={isWin ? C.green : C.red}>{isWin ? 'WIN' : 'LOSS'}</Pill>}
            </div>
            <div style={{ fontFamily: C.font, fontSize: 10, color: C.meta, marginTop: 5 }}>
              {fmtDate(position.entryTime)} · {fmtTime(position.entryTime)}
              {position.exitTime ? ` → ${fmtTime(position.exitTime)}` : ' · Active'}
              {' · '}{holdDisplay} hold
            </div>
          </Modal.Title>
        </Modal.Header>

        {/* ── Body ── */}
        <Modal.Body style={{ background: C.bg, padding: '20px 24px', maxHeight: '80vh', overflowY: 'auto' }}>

          {/* P&L Hero */}
          <div style={{
            background: isWin ? `${C.green}12` : `${C.red}12`,
            border: `1px solid ${isWin ? C.green : C.red}40`,
            borderRadius: 10, padding: '16px 24px', marginBottom: 12,
            display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <div>
              <Label>{isClosed ? 'FINAL P&L' : 'UNREALIZED P&L'}</Label>
              <div style={{ fontFamily: C.font, fontSize: 28, fontWeight: 600, color: isWin ? C.green : C.red }}>
                {isWin ? '+' : ''}${fmtNum(unrealizedPL)}
              </div>
            </div>
            <div>
              <Label>RETURN</Label>
              <div style={{ fontFamily: C.font, fontSize: 20, fontWeight: 500, color: isWin ? C.green : C.red }}>
                {isWin ? '+' : ''}{fmtNum(plPct)}%
              </div>
            </div>
            <div>
              <Label>PRICE MOVEMENT</Label>
              <div style={{ fontFamily: C.font, fontSize: 13, color: C.body }}>
                ${fmtNum(entryPrice)}
                <span style={{ color: C.meta, margin: '0 8px' }}>→</span>
                ${fmtNum(isClosed ? exitPrice : currentPrice)}
              </div>
            </div>
            <div>
              <Label>HOLD TIME</Label>
              <Value size={16}>{holdDisplay}</Value>
            </div>
          </div>

          {/* Position Details */}
          <Section title="POSITION DETAILS">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 16 }}>
              <div><Label>ENTRY TIME</Label><Value>{fmtTime(position.entryTime)}</Value></div>
              <div><Label>ENTRY PRICE</Label><Value size={16}>${fmtNum(entryPrice)}</Value></div>
              <div><Label>QUANTITY</Label><Value size={16}>{qty}</Value></div>
              <div><Label>ENTRY VALUE</Label><Value>${fmtNum(entryPrice * qty)}</Value></div>
              {isClosed && <>
                <div><Label>EXIT TIME</Label><Value>{fmtTime(position.exitTime)}</Value></div>
                <div><Label>EXIT PRICE</Label><Value size={16}>${fmtNum(exitPrice)}</Value></div>
                <div><Label>EXIT VALUE</Label><Value>${fmtNum(exitPrice * qty)}</Value></div>
              </>}
              {!isClosed && <>
                <div><Label>CURRENT PRICE</Label><Value size={16}>${fmtNum(currentPrice)}</Value></div>
                <div><Label>CURRENT VALUE</Label><Value>${fmtNum(currentPrice * qty)}</Value></div>
              </>}
              {position.target > 0 && (
                <div><Label>TARGET</Label><Value color={C.green}>${fmtNum(position.target)}</Value></div>
              )}
              {position.stop > 0 && (
                <div><Label>STOP LOSS</Label><Value color={C.red}>${fmtNum(position.stop)}</Value></div>
              )}
              {position.rrRatio > 0 && (
                <div><Label>RISK / REWARD</Label><Value>1:{fmtNum(position.rrRatio, 1)}</Value></div>
              )}
            </div>
          </Section>

          {/* Chart */}
          {chartData?.length > 0 && (
            <Section title="POSITION CHART">
              <div style={{ height: 600, width: '100%' }}>
                <MarketChart data={chartData} selectedPosition={position} compact={true} />
              </div>
            </Section>
          )}

          {/* Evaluations */}
          <Section title={`AGENT EVALUATIONS · ${positionEvaluations.length} TOTAL · ${proactiveEvals.length} PROACTIVE · ${reactiveEvals.length} REACTIVE`}>
            {positionEvaluations.length === 0 ? (
              (() => {
                const msg = evalEmptyMessage();
                return (
                  <div style={{
                    background: C.bg3, border: `1px solid ${C.border}`,
                    borderRadius: 8, padding: '20px 24px',
                  }}>
                    <div style={{ fontFamily: C.font, fontSize: 12, color: C.body, fontWeight: 500, marginBottom: 6 }}>
                      {msg.title}
                    </div>
                    <div style={{ fontFamily: C.font, fontSize: 10, color: C.meta, lineHeight: 1.7 }}>
                      {msg.detail}
                    </div>
                  </div>
                );
              })()
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {positionEvaluations.map((ev, idx) => {
                  const isExpanded = expandedEval === (ev.id || idx);
                  const dCol       = decisionColor(ev.decision);
                  const phaseCol   = ev.type === 'REACTIVE' ? C.blue : C.meta;
                  const reasoning  = ev.reasoning || ev.fullData?.eval_reasoning || ev.fullData?.eval_narrative_summary || '';

                  return (
                    <div
                      key={ev.id || idx}
                      style={{
                        background: C.bg3,
                        border: `1px solid ${C.border}`,
                        borderLeft: `3px solid ${phaseCol}`,
                        borderRadius: 8,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Eval row header */}
                      <div
                        onClick={() => setExpandedEval(isExpanded ? null : (ev.id || idx))}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '12px 16px', cursor: 'pointer',
                          justifyContent: 'space-between', flexWrap: 'wrap',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {/* Phase pill */}
                          <span style={{
                            fontFamily: C.font, fontSize: 8, letterSpacing: '0.8px',
                            color: phaseCol, background: `${phaseCol}18`,
                            border: `1px solid ${phaseCol}40`,
                            borderRadius: 3, padding: '2px 6px',
                          }}>
                            {ev.type}
                          </span>
                          {/* Decision pill */}
                          <span style={{
                            fontFamily: C.font, fontSize: 9,
                            color: dCol, background: `${dCol}18`,
                            border: `1px solid ${dCol}40`,
                            borderRadius: 3, padding: '2px 8px', fontWeight: 600,
                          }}>
                            {decisionIcon(ev.decision)} {ev.decision}
                          </span>
                          {/* Time */}
                          <span style={{ fontFamily: C.font, fontSize: 9, color: C.meta }}>
                            {fmtTime(ev.timestamp)}
                          </span>
                          {/* Confidence */}
                          {ev.confidence && (
                            <span style={{ fontFamily: C.font, fontSize: 9, color: C.meta }}>
                              Conf {ev.confidence}%
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {/* View full details button */}
                          {onEvaluationSelect && (
                            <button
                              onClick={e => { e.stopPropagation(); onEvaluationSelect(ev); }}
                              style={{
                                fontFamily: C.font, fontSize: 9,
                                color: C.cyan, background: `${C.cyan}15`,
                                border: `1px solid ${C.cyan}40`,
                                borderRadius: 4, padding: '2px 10px', cursor: 'pointer',
                              }}
                            >
                              Full details →
                            </button>
                          )}
                          {/* Expand toggle */}
                          <span style={{ fontFamily: C.font, fontSize: 9, color: C.meta }}>
                            {isExpanded ? '▲' : '▼'}
                          </span>
                        </div>
                      </div>

                      {/* Reasoning preview (always visible) */}
                      {reasoning && !isExpanded && (
                        <div style={{
                          padding: '0 16px 12px',
                          fontFamily: C.font, fontSize: 10, color: C.body,
                          lineHeight: 1.7, opacity: 0.8,
                        }}>
                          {reasoning.substring(0, 180)}{reasoning.length > 180 ? '…' : ''}
                        </div>
                      )}

                      {/* Expanded reasoning */}
                      {isExpanded && (
                        <div style={{
                          borderTop: `1px solid ${C.border}`,
                          padding: '14px 16px',
                          background: C.bg2,
                        }}>
                          {/* Full reasoning */}
                          {reasoning && (
                            <div style={{ marginBottom: 14 }}>
                              <div style={{ fontFamily: C.font, fontSize: 9, color: C.meta, letterSpacing: '0.8px', marginBottom: 6 }}>REASONING</div>
                              <div style={{
                                fontFamily: C.font, fontSize: 10, color: C.body,
                                lineHeight: 1.8, whiteSpace: 'pre-wrap',
                                background: C.bg, border: `1px solid ${C.border}`,
                                borderRadius: 6, padding: '10px 14px',
                              }}>
                                {reasoning}
                              </div>
                            </div>
                          )}

                          {/* Decision detail from agent story */}
                          {(() => {
                            const story = ev.fullData?.eval_agent_story || {};
                            const dec   = story.decision || {};
                            const perf  = story.performance || {};
                            const mem   = story.memory_summary || story.memory || {};
                            const ra    = story.chart_analysis?.llm_full_response?.regime_analysis || {};

                            return (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                                {dec.new_target != null && (
                                  <div>
                                    <Label>TARGET SET</Label>
                                    <Value color={C.green}>${fmtNum(dec.new_target)}</Value>
                                  </div>
                                )}
                                {dec.new_stop_loss != null && (
                                  <div>
                                    <Label>STOP SET</Label>
                                    <Value color={C.red}>${fmtNum(dec.new_stop_loss)}</Value>
                                  </div>
                                )}
                                {dec.confidence != null && (
                                  <div>
                                    <Label>CONFIDENCE</Label>
                                    <Value>{fmtNum(dec.confidence * 100, 0)}%</Value>
                                  </div>
                                )}
                                {perf.target_hit_rate != null && perf.target_hit_rate > 0 && (
                                  <div>
                                    <Label>HIST PROFITABLE</Label>
                                    <Value color={perf.target_hit_rate >= 0.6 ? C.green : C.amber}>
                                      {fmtNum(perf.target_hit_rate * 100, 0)}%
                                    </Value>
                                  </div>
                                )}
                                {mem.top_similarity > 0 && (
                                  <div>
                                    <Label>MEMORY MATCH</Label>
                                    <Value>{fmtNum((mem.top_similarity || mem.best_match) * 100, 0)}%</Value>
                                  </div>
                                )}
                                {ra.regime_classification && (
                                  <div>
                                    <Label>REGIME</Label>
                                    <Value>{ra.regime_classification}</Value>
                                  </div>
                                )}
                                {ev.execution_time != null && (
                                  <div>
                                    <Label>EVAL TIME</Label>
                                    <Value>{fmtNum(ev.execution_time, 1)}s</Value>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

        </Modal.Body>

        {/* ── Footer ── */}
        <Modal.Footer style={{ background: C.bg1, borderTop: `1px solid ${C.border}`, padding: '12px 24px' }}>
          {onViewFullStory && positionEvaluations.length > 0 && (
            <button
              onClick={onViewFullStory}
              style={{
                fontFamily: C.font, fontSize: 10,
                color: C.cyan, background: `${C.cyan}15`,
                border: `1px solid ${C.cyan}40`,
                borderRadius: 5, padding: '6px 16px', cursor: 'pointer',
                marginRight: 'auto',
              }}
            >
              View Full Agent Story →
            </button>
          )}
          <button
            onClick={onHide}
            style={{
              fontFamily: C.font, fontSize: 10,
              color: C.body, background: 'transparent',
              border: `1px solid ${C.border}`,
              borderRadius: 5, padding: '6px 16px', cursor: 'pointer',
            }}
          >
            Close
          </button>
        </Modal.Footer>

      </div>
    </Modal>
  );
};

export default PositionDetailModal;